import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import * as api from '../lib/api';
import { useAuthStore } from '../lib/store';
import { isDesktop } from '../lib/desktop';
import { trackEvent } from '../lib/analytics';
import { BrandLockup } from '../components/BrandMark';

/**
 * Landing page for the social-login redirect. The worker hands us a short-lived,
 * single-use `code` which we immediately exchange for real session tokens (or an
 * MFA handoff), then route the user onward.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = searchParams.get('code');

    // Strip the one-time code from the address bar so it can't linger in
    // history. The desktop build routes in the URL fragment (HashRouter),
    // so rewrite the hash there instead of the pathname.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState({}, '', isDesktop ? '#/auth/callback' : '/auth/callback');
    }

    if (!code) {
      navigate(
        `/login?oauth_error=${encodeURIComponent('Sign-in was cancelled')}`,
        { replace: true },
      );
      return;
    }

    (async () => {
      try {
        const result = await api.exchangeOAuthCode(code);

        // 2FA-protected account: resume the MFA step on the login page.
        if (result.mfaRequired && result.mfaToken) {
          navigate('/login', {
            replace: true,
            state: {
              oauthMfa: { mfaToken: result.mfaToken, email: result.user?.email },
            },
          });
          return;
        }

        if (!result.accessToken || !result.refreshToken) {
          throw new Error('Login failed');
        }

        if (result.isNewUser) {
          trackEvent('sign_up', { method: 'oauth' });
        }

        const { setTokens, setUser, setOrganizations, setCurrentOrganization } =
          useAuthStore.getState();
        setTokens(result.accessToken, result.refreshToken);
        const userData = await api.getCurrentUser();
        setOrganizations(userData.organizations || []);
        setCurrentOrganization(userData.organizations?.[0] ?? null);
        setUser(userData);

        const hasOrg = (userData.organizations?.length ?? 0) > 0;
        let destination: string;
        if (result.mode === 'signup') {
          destination = hasOrg ? '/dashboard' : '/onboarding';
        } else {
          destination = result.returnTo || (hasOrg ? '/dashboard' : '/onboarding');
          if (destination.startsWith('/admin') && !userData.isSiteAdmin) {
            destination = '/dashboard';
          }
        }
        navigate(destination, { replace: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setError(message);
        navigate(`/login?oauth_error=${encodeURIComponent(message)}`, {
          replace: true,
        });
      }
    })();
  }, [navigate, searchParams]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      <BrandLockup
        iconClassName="h-10 w-10"
        textClassName="text-2xl font-bold text-slate-900 dark:text-white"
      />
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : (
        <div className="flex items-center gap-3 text-slate-600 dark:text-neutral-400">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <span>Signing you in…</span>
        </div>
      )}
    </div>
  );
}
